import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing, CheckCircle2, Clock, Loader2, MessageCircle, Search, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { waUrl } from '../../../lib/waShare';
import { formatErr } from '../billing/shared';

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : '');
const chip = (on) => `rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? 'border-teal-700 bg-teal-700 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'}`;
const KINDS = [['all', 'All invoices'], ['gst', 'GST'], ['invoice', 'Non-GST']];
const STATUSES = [['pending', 'Pending'], ['sent', 'Sent'], ['remind', 'Needs reminder'], ['rated', 'Rated'], ['all', 'All']];

const StatusBadge = ({ r }) => {
  if (r.status === 'rated') return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`rs-status-${r.invoice_id}`}><Star size={11} fill="currentColor" /> Rated {r.rating}★</span>;
  if (r.status === 'sent') return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`rs-status-${r.invoice_id}`}>
      <CheckCircle2 size={11} /> Sent {when(r.wa_sent_at || r.email_sent_at)}{r.send_count > 1 ? ` · ×${r.send_count}` : ''}{!r.wa_sent_at && r.email_sent_at ? ' (email)' : ''}
    </span>
  );
  return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600" data-testid={`rs-status-${r.invoice_id}`}><Clock size={11} /> Pending</span>;
};

// Real links (not window.open after a fetch): iOS/iPadOS and Android only hand a user-tapped link to WhatsApp.
const WaLink = ({ href, onOpen, disabled, testid, className, style, children, title }) => (disabled || !href
  ? <span aria-disabled="true" title={title} data-testid={testid} className={`${className} cursor-not-allowed opacity-40`} style={style}>{children}</span>
  : <a href={href} target="_blank" rel="noopener noreferrer" onClick={onOpen} data-testid={testid} className={className} style={style}>{children}</a>);

const RemindButton = ({ r, onRemind }) => (
  <WaLink href={r.reminder_message ? waUrl(r.phone, r.reminder_message) : ''} disabled={!r.needs_reminder} onOpen={() => onRemind(r)}
    testid={`rs-remind-${r.invoice_id}`} title={r.reminded_at ? 'Only one reminder per invoice' : ''}
    className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100">
    <BellRing size={13} /> {r.reminded_at ? 'Reminded' : 'Send reminder'}
  </WaLink>
);

const SEND_CLS = 'inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold hover:bg-emerald-700';

const Row = ({ r, checked, onCheck, onSend, onRemind }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`rs-row-${r.invoice_id}`}>
    <input type="checkbox" checked={checked} disabled={!r.message} onChange={() => onCheck(r.invoice_id)} className="h-4 w-4 accent-teal-700" data-testid={`rs-check-${r.invoice_id}`} />
    <div className="min-w-0 basis-[calc(100%-2rem)] sm:basis-auto sm:flex-1">
      <p className="text-sm font-semibold text-slate-900">
        {r.customer_name || 'Walk-in customer'}
        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">{r.kind === 'gst' ? 'GST' : 'Non-GST'}</span>
      </p>
      <p className="text-xs text-slate-500">{[r.number, day(r.date), r.vehicle, r.phone || 'no mobile number'].filter(Boolean).join(' · ')}</p>
    </div>
    <div className="ml-7 flex flex-1 flex-wrap items-center justify-between gap-2 sm:ml-0 sm:flex-none sm:justify-end">
    <StatusBadge r={r} />
    {r.reminded_at && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`rs-reminded-${r.invoice_id}`}><BellRing size={11} /> Reminded {when(r.reminded_at)}</span>}
    {(r.needs_reminder || r.reminded_at) && <RemindButton r={r} onRemind={onRemind} />}
    <WaLink href={r.message ? waUrl(r.phone, r.message) : ''} onOpen={() => onSend(r)} testid={`rs-send-${r.invoice_id}`} className={SEND_CLS} style={{ color: '#fff' }}>
      <MessageCircle size={14} /> {r.status === 'pending' ? 'Send on WhatsApp' : 'Resend'}
    </WaLink>
    </div>
  </div>
);

const QueueBar = ({ queue, onNext, onStop }) => {
  const cur = queue.items[queue.pos];
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="rs-queue">
      <p className="flex-1 text-sm text-emerald-900"><b>{queue.pos + 1} of {queue.items.length}:</b> {cur.customer_name || cur.number}</p>
      <WaLink href={waUrl(cur.phone, cur.message)} onOpen={onNext} testid="rs-queue-open" className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold" style={{ color: '#fff' }}>Open WhatsApp</WaLink>
      <button type="button" onClick={onStop} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-900" data-testid="rs-queue-stop"><X size={14} /> Stop</button>
    </div>
  );
};

export const ReviewSender = ({ onCounts }) => {
  const { authHeader } = useAuth();
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [queue, setQueue] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/feedback/invoices`, { headers: authHeader(), params: { kind, status, q } });
      setData(d); onCounts?.(d.counts);
    } catch (e) { toast.error(formatErr(e)); }
  }, [authHeader, kind, status, q, onCounts]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // The link opens WhatsApp itself; this only records the send.
  const send = (r) => {
    const now = new Date().toISOString();
    setData((d) => {
      const counts = r.status === 'pending' ? { ...d.counts, pending: d.counts.pending - 1, sent: d.counts.sent + 1 } : d.counts;
      if (counts !== d.counts) onCounts?.(counts);
      return { ...d, counts, rows: d.rows.map((x) => (x.invoice_id === r.invoice_id
        ? { ...x, status: x.status === 'rated' ? 'rated' : 'sent', wa_sent_at: now, send_count: (x.send_count || 0) + 1 } : x)) };
    });
    setPicked((p) => p.filter((id) => id !== r.invoice_id));
    axios.post(`${API}/feedback/${r.request_id}/whatsapp-sent`, {}, { headers: authHeader() }).catch((e) => toast.error(formatErr(e)));
  };

  const remind = (r) => {
    setData((d) => {
      const counts = { ...d.counts, remind: d.counts.remind - 1 };
      onCounts?.(counts);
      return { ...d, counts, rows: d.rows.map((x) => (x.invoice_id === r.invoice_id ? { ...x, reminded_at: new Date().toISOString(), needs_reminder: false } : x)) };
    });
    axios.post(`${API}/feedback/${r.request_id}/remind`, {}, { headers: authHeader() }).catch((e) => toast.error(formatErr(e)));
  };

  const nextInQueue = () => {
    send(queue.items[queue.pos]);
    setQueue((qu) => (qu.pos + 1 < qu.items.length ? { ...qu, pos: qu.pos + 1 } : null));
  };

  if (!data) return <div className="p-10 text-center text-slate-500"><Loader2 className="inline animate-spin" /></div>;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const sendable = data.rows.filter((r) => r.message);
  const allPicked = sendable.length > 0 && sendable.every((r) => picked.includes(r.invoice_id));
  return (
    <div className="pb-16" data-testid="review-sender">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {KINDS.map(([k, l]) => <button key={k} type="button" className={chip(kind === k)} onClick={() => setKind(k)} data-testid={`rs-kind-${k}`}>{l}</button>)}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        {STATUSES.map(([k, l]) => <button key={k} type="button" className={chip(status === k)} onClick={() => setStatus(k)} data-testid={`rs-status-filter-${k}`}>{l} ({data.counts[k] ?? 0})</button>)}
        <label className="ml-auto flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1">
          <Search size={14} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice, customer, phone, vehicle" className="w-56 bg-transparent text-xs outline-none" data-testid="rs-search" />
        </label>
      </div>
      {queue && <QueueBar queue={queue} onNext={nextInQueue} onStop={() => setQueue(null)} />}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="rs-list">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2">
          <input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? [] : sendable.map((r) => r.invoice_id))} className="h-4 w-4 accent-teal-700" data-testid="rs-check-all" />
          <span className="flex-1 text-xs font-semibold text-slate-600">{picked.length ? `${picked.length} selected` : `${data.rows.length} invoice${data.rows.length === 1 ? '' : 's'}`}</span>
          <button type="button" disabled={!picked.length || !!queue} data-testid="rs-send-selected"
            onClick={() => setQueue({ items: data.rows.filter((r) => picked.includes(r.invoice_id)), pos: 0 })}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40" style={{ color: '#fff', backgroundColor: '#0f172a' }}>
            <MessageCircle size={13} /> Send to selected ({picked.length})
          </button>
        </div>
        {data.rows.length === 0 && <p className="p-6 text-center text-sm text-slate-500" data-testid="rs-empty">No invoices match.</p>}
        {data.rows.map((r) => <Row key={r.invoice_id} r={r} checked={picked.includes(r.invoice_id)} onCheck={toggle} onSend={send} onRemind={remind} />)}
      </div>
    </div>
  );
};
